import { useState, useEffect } from 'react';
import { X, Check, Pill } from 'lucide-react';
import { Card } from '../ui/Card';
import axios from 'axios';
import toast from 'react-hot-toast';

interface Medication {
  id: string;
  name: string;
  dose: string;
  frequency: string;
}

interface LogMedicationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

// Returns local datetime string in format "YYYY-MM-DDTHH:mm" (for datetime-local input)
function getLocalDateTimeString(date = new Date()) {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function LogMedicationModal({ isOpen, onClose, onSuccess }: LogMedicationModalProps) {
  const [medications, setMedications] = useState<Medication[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [takenAt, setTakenAt] = useState(getLocalDateTimeString());
  const [skipped, setSkipped] = useState(false);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadMedications();
      // Reset form on open
      setSelectedIds(new Set());
      setTakenAt(getLocalDateTimeString());
      setSkipped(false);
      setNotes('');
    }
  }, [isOpen]);

  const loadMedications = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${import.meta.env.VITE_API_URL}/api/profile/medications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMedications(response.data || []);
    } catch (error) {
      console.error('Error loading medications:', error);
      toast.error('Eroare la încărcarea medicamentelor');
    } finally {
      setLoading(false);
    }
  };

  const toggleMedication = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const selectAll = () => {
    setSelectedIds(new Set(medications.map(m => m.id)));
  };

  const deselectAll = () => {
    setSelectedIds(new Set());
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (selectedIds.size === 0) {
      toast.error('Selectează cel puțin un medicament');
      return;
    }

    try {
      setSubmitting(true);
      const token = localStorage.getItem('accessToken');
      const isoDateTime = new Date(takenAt).toISOString();

      // Log all selected medications in parallel
      await Promise.all(
        Array.from(selectedIds).map(medicationId =>
          axios.post(
            `${import.meta.env.VITE_API_URL}/api/tracking/medications`,
            {
              medicationId,
              takenAt: isoDateTime,
              skipped,
              notes
            },
            { headers: { Authorization: `Bearer ${token}` } }
          )
        )
      );

      const count = selectedIds.size;
      toast.success(
        skipped
          ? `${count} medicament${count > 1 ? 'e marcate' : ' marcat'} ca sărit${count > 1 ? 'e' : ''}`
          : `${count} medicament${count > 1 ? 'e înregistrate' : ' înregistrat'} cu succes`
      );
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error logging medications:', error);
      toast.error(error.response?.data?.error || 'Eroare la înregistrare');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const allSelected = medications.length > 0 && selectedIds.size === medications.length;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold flex items-center gap-2">
              💊 Log Medicamente
            </h3>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">

            {/* Medication Multi-Select */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Selectează medicamentele *
                </label>
                {medications.length > 1 && (
                  <button
                    type="button"
                    onClick={allSelected ? deselectAll : selectAll}
                    className="text-xs text-blue-600 hover:text-blue-800 font-medium transition"
                  >
                    {allSelected ? 'Deselectează toate' : 'Selectează toate'}
                  </button>
                )}
              </div>

              {loading ? (
                <div className="text-sm text-gray-500 py-3 text-center">Se încarcă...</div>
              ) : medications.length === 0 ? (
                <div className="text-sm text-gray-500 py-3">
                  Nu ai medicamente adăugate.{' '}
                  <a href="/profile/medications" className="text-blue-600 hover:underline">
                    Adaugă medicamente
                  </a>
                </div>
              ) : (
                <div className="space-y-2 max-h-52 overflow-y-auto border border-gray-200 rounded-lg p-2">
                  {medications.map((med) => {
                    const isSelected = selectedIds.has(med.id);
                    return (
                      <button
                        key={med.id}
                        type="button"
                        onClick={() => toggleMedication(med.id)}
                        className={`w-full flex items-center gap-3 p-3 rounded-lg border-2 transition text-left ${
                          isSelected
                            ? 'border-green-500 bg-green-50'
                            : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {/* Custom checkbox */}
                        <div
                          className={`w-5 h-5 rounded flex items-center justify-center shrink-0 border-2 transition ${
                            isSelected
                              ? 'bg-green-500 border-green-500'
                              : 'border-gray-300 bg-white'
                          }`}
                        >
                          {isSelected && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                        </div>
                        <Pill className={`w-4 h-4 shrink-0 ${isSelected ? 'text-green-600' : 'text-gray-400'}`} />
                        <div className="flex-1 min-w-0">
                          <div className={`text-sm font-medium truncate ${isSelected ? 'text-green-800' : 'text-gray-800'}`}>
                            {med.name}
                          </div>
                          <div className={`text-xs truncate ${isSelected ? 'text-green-600' : 'text-gray-500'}`}>
                            {med.dose}{med.frequency ? ` · ${med.frequency}` : ''}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Selection counter */}
              {medications.length > 0 && (
                <p className="text-xs text-gray-500 mt-1.5">
                  {selectedIds.size === 0
                    ? 'Niciun medicament selectat'
                    : `${selectedIds.size} din ${medications.length} medicament${medications.length > 1 ? 'e' : ''} selectat${selectedIds.size > 1 ? 'e' : ''}`}
                </p>
              )}
            </div>

            {/* Date/Time */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Data și ora
              </label>
              <input
                type="datetime-local"
                value={takenAt}
                onChange={(e) => setTakenAt(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            {/* Skipped Checkbox */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="skipped"
                checked={skipped}
                onChange={(e) => setSkipped(e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="skipped" className="ml-2 text-sm text-gray-700">
                Am sărit doza
              </label>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Note (opțional)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                placeholder="Adaugă note despre administrare..."
              />
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
              >
                Anulează
              </button>
              <button
                type="submit"
                disabled={submitting || medications.length === 0 || selectedIds.size === 0}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Se salvează...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    {selectedIds.size > 1
                      ? `Adaugă ${selectedIds.size} medicamente`
                      : 'Adaugă la Log'}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </Card>
    </div>
  );
}
